import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import styles from './AuthModal.module.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup';
}

export function AuthModal({ isOpen, onClose, initialMode = 'signin' }: AuthModalProps) {
  const { signInWithApple, signInWithEmail, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Validate username (same rules as mobile)
  const validateUsername = (value: string): { valid: boolean; error?: string } => {
    if (!value || typeof value !== 'string') {
      return { valid: false, error: 'Username is required' };
    }

    const trimmed = value.trim();

    if (trimmed.length < 3) {
      return { valid: false, error: 'Username must be at least 3 characters' };
    }

    if (trimmed.length > 20) {
      return { valid: false, error: 'Username must be 20 characters or less' };
    }

    if (!/^[a-zA-Z0-9_ '-]+$/.test(trimmed)) {
      return { valid: false, error: 'Username can only contain letters, numbers, spaces, underscores, hyphens, and apostrophes' };
    }

    return { valid: true };
  };

  const handleAppleAuth = async () => {
    // For sign up mode, validate username first
    if (mode === 'signup') {
      const validation = validateUsername(username);
      if (!validation.valid) {
        setError(validation.error || 'Invalid username');
        return;
      }
      // Note: Apple OAuth will redirect, so we can't create profile here
      // The profile will need to be created after redirect in AuthCallback
      // For now, we'll store the username in sessionStorage
      sessionStorage.setItem('pendingUsername', username.trim());
    }

    setLoading(true);
    setError('');

    try {
      await signInWithApple();
      // Note: This won't actually reach here due to redirect
    } catch (err) {
      console.error('Apple auth error:', err);
      setError(err instanceof Error ? err.message : 'Apple Sign-In failed');
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    // Validation
    if (mode === 'signup') {
      const validation = validateUsername(username);
      if (!validation.valid) {
        setError(validation.error || 'Invalid username');
        return;
      }
    }

    if (!email.trim()) {
      setError('Please enter an email');
      return;
    }

    if (!password) {
      setError('Please enter a password');
      return;
    }

    if (mode === 'signup') {
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        await signUp(email.trim(), password, username.trim());
        onClose();
      } else {
        await signInWithEmail(email.trim(), password);
        onClose();
      }
    } catch (err) {
      console.error('Email auth error:', err);
      const message = err instanceof Error ? err.message : 'Authentication failed';

      // User-friendly error messages
      if (message.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else if (message.includes('already registered')) {
        setError('Email already registered. Try signing in instead.');
        setMode('signin');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {/* Username Input - Only in Sign Up mode */}
          {mode === 'signup' && (
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="username">
                Username
              </label>
              <input
                id="username"
                type="text"
                className={styles.input}
                placeholder="Choose a username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                disabled={loading}
                maxLength={20}
                autoComplete="username"
              />
              <span className={styles.hint}>
                3-20 characters
              </span>
            </div>
          )}

          {/* Section Title */}
          <h3 className={styles.sectionTitle}>
            {mode === 'signup' ? 'Sign up with' : 'Sign in with'}
          </h3>

          {/* Apple Sign-In Button */}
          <button
            className={styles.appleButton}
            onClick={handleAppleAuth}
            disabled={loading}
          >
            <svg className={styles.appleIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {mode === 'signup' ? 'Sign up with Apple' : 'Sign in with Apple'}
          </button>

          {/* Divider */}
          <div className={styles.divider}>
            <span className={styles.dividerLine}></span>
            <span className={styles.dividerText}>or</span>
            <span className={styles.dividerLine}></span>
          </div>

          {/* Email Input */}
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={styles.input}
              placeholder="your@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              disabled={loading}
              autoComplete="email"
            />
          </div>

          {/* Password Input */}
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className={styles.input}
              placeholder={mode === 'signup' ? 'Create a password (min 6 characters)' : 'Enter your password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              disabled={loading}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>

          {/* Confirm Password - Only in Sign Up mode */}
          {mode === 'signup' && (
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="confirmPassword">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={styles.input}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
          )}

          {/* Error Message */}
          {error && <p className={styles.error}>{error}</p>}

          {/* Submit Button */}
          <button
            className={styles.submitButton}
            onClick={handleEmailAuth}
            disabled={loading}
          >
            {loading ? 'Please wait...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
          </button>

          {/* Toggle Mode */}
          <button className={styles.toggleButton} onClick={toggleMode} disabled={loading}>
            {mode === 'signup'
              ? 'Already have an account? Sign in'
              : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
}
